/** Billing screen (tenant/billing.html). */
export const billing = {
  title: "Billing & Usage",
  subtitle: "Your plan, what you've used this cycle ({{cycle}}), and official receipts.",
  cycleRange: "{{start}} – {{end}}",
  exportUsage: "Export usage",
  upgradePlan: "Upgrade plan",
  perMonth: "/ month",
  perYear: "/ year",
  loadError: "Billing couldn't load.",
  loadErrorBody: "Your plan and payments are safe. Try again in a moment.",
  gateway: {
    legend: "Pay with",
    paymongo: "GCash, Maya or card",
    stripe: "Card",
    paypal: "PayPal",
    hint_paymongo: "Secure checkout by PayMongo",
    hint_stripe: "Visa, Mastercard, Amex, Apple Pay or Google Pay · secure checkout by Stripe",
    hint_paypal: "PayPal balance or a card in your PayPal account",
    name_paymongo: "PayMongo",
    name_stripe: "Stripe",
    name_paypal: "PayPal",
    save: "Keep this method for renewals",
    saveHint: "Saved securely by the payment provider; remove it anytime under Payment method.",
    testMode: "Test mode — no real money is charged.",
  },
  region: {
    changeBanner: "Your billing currency changes from {{current}} to {{currency}} on {{date}}, when your next billing period starts.",
  },
  gate: {
    badge: "Upgrade required",
    upgrade: "See plans",
    title: "Billing isn't available on this workspace",
    body: "Ask CRUD.IT to enable self-serve billing for your plan.",
  },
  status: {
    active: "Active",
    trialing: "Trial",
    past_due: "Past due",
    paused: "Paused",
    canceled: "Canceled",
  },
  plan: {
    current: "Current plan",
    billedMonthly: "Billed monthly",
    billedAnnually: "Billed yearly",
    renews: "renews",
    notIncluded: "{{features}}: {{plan}}",
    change: "Change plan",
    switchAnnual: "Switch to yearly · save 2 months",
    annualActive: "Yearly billing",
    graceUntil: "You've used all messages this cycle. The assistant keeps answering until {{date}}, then visitors are asked to contact you directly. Top up or upgrade to keep it running.",
    scheduled: "Your plan changes to {{plan}} on {{date}}.",
    scheduledCredit: "{{amount}} credit goes to your next invoice.",
    keepPlan: "Keep current plan",
    scheduledCancelled: "Scheduled change cancelled. You stay on your current plan.",
  },
  usage: {
    title: "Usage this cycle",
    cycle: "Day {{day}} of {{days}} · resets {{date}}",
    projectedLabel: "Projected:",
    projected_one: "~{{value}} message",
    projected_other: "~{{value}} messages",
    messages: "Messages",
    agents: "Agents",
    storage: "Knowledge storage",
    seats: "Seats",
    playground: "Playground test messages",
    playgroundHint: "Separate pool for testing in the Playground; doesn't use your message allowance.",
    workflowRuns: "Workflow runs",
    workflowRunsHint: "Runs this month. Past the limit, background workflows skip; customer-facing ones get a 10% grace first.",
    voice: "Voice minutes",
    notOnPlan: "Not on plan",
    unlimited: "Unlimited",
    remaining: "{{value}} left this cycle",
    overPace: "On pace to exceed by ~{{value}} before {{date}}",
    addSeats: "Add seats",
    perSeat: "at {{price}}/seat/month",
    perSeat_year: "at {{price}}/seat/year",
    addStorage: "Add storage",
    perGb: "at {{price}}/GB/month",
    perGb_year: "at {{price}}/GB/year",
    overageTitle: "Overage policy:",
    overageBody: "when you hit {{value}} messages, the agent keeps answering for 24 hours and we email the Owner. After that, visitors see \"Please contact us directly\" until you top up, upgrade, or the cycle resets.",
  },
  topup: {
    title: "Auto top-up",
    pack: "{{price}} per {{credits}} extra messages",
    settingsTitle: "Auto top-up settings",
    packs: "Packs per top-up",
    packsHint: "1 to {{max}} packs each time.",
    packsError: "Choose 1 to {{max}} packs.",
    threshold: "Top up when fewer than",
    thresholdHint: "Messages left in the cycle.",
    cap: "Monthly spending cap ({{currency}})",
    capHint: "Optional. Leave empty for no cap.",
    numberError: "Enter a valid number.",
    grace: "24-hour overage grace",
    graceHint: "Keep answering for 24 hours after the allowance runs out.",
    saveOn: "Save and turn on",
    on: "Auto top-up is on",
    off: "Auto top-up turned off",
  },
  plans: {
    title: "Plans",
    subtitleExclusive: "Prices in {{currency}}, before tax ({{tax}} added on invoices). Official receipts issued for every payment.",
    subtitleOther: "Prices in {{currency}} · {{tax}}. Official receipts issued for every payment.",
    period: "Billing period",
    monthly: "Monthly",
    yearly: "Yearly",
    yearlyFree_one: "Yearly · {{count}} month free",
    yearlyFree_other: "Yearly · {{count}} months free",
    current: "Current plan",
    recommended: "Recommended",
    custom: "Custom",
    customVolume: "Custom volume and seats",
    messages: "{{value}} messages / month",
    agents_one: "{{count}} agent",
    agents_other: "{{count}} agents",
    storage: "{{value}} knowledge",
    seats_one: "{{count}} seat",
    seats_other: "{{count}} seats",
    yourPlan: "Your plan",
    upgradeTo: "Upgrade to {{plan}}",
    downgrade: "Downgrade",
    switchYearly: "Switch to yearly",
    switchMonthly: "Switch to monthly",
    contactSales: "Contact sales",
    payManually: "Pay manually / talk to sales",
    salesSubject: "{{plan}} plan inquiry",
    tagline_starter: "Small organizations getting started",
    tagline_standard: "Most growing organizations",
    tagline_pro: "Multi-branch and larger teams",
    tagline_institution: "Large institutions, LGUs and networks",
  },
  features: {
    widget: "Website widget",
    webapp: "web app",
    messenger: "Facebook Messenger",
    actions: "Actions",
    helpdesk: "helpdesk inbox",
    api: "API",
    custom_roles: "custom roles",
    evaluations: "evaluations",
    mobile_white_label: "mobile app",
    remove_branding: "white-label widget",
    sso: "SSO",
    secure_launch: "Secure launch",
    secure_launch_facts: "customer facts in chat",
    after_sales: "after-sales desk",
    commerce: "orders and catalogue",
    commerce_presets: "commerce presets",
    connectors: "connectors",
    workflows: "workflows",
    graph_retrieval: "knowledge graph search",
    customer_success_manager: "Dedicated success manager",
    private_deployment: "On-prem / private deployment option",
    sla: "99.9% uptime SLA",
  },
  limits: {
    messages: "{{value}} messages",
    agents: "{{value}} agents",
    storage: "{{value}} knowledge",
    seats: "{{value}} seats",
  },
  change: {
    titleUpgrade: "Upgrade to {{plan}}",
    titleDowngrade: "Downgrade to {{plan}}",
    titleInterval: "Switch {{plan}} to yearly billing",
    upgradeBody: "You get {{limits}} right away.",
    downgradeBody: "{{plan}} allows {{agents}} agents, {{seats}} seats and {{storage}}. You currently use {{usedAgents}} agents, {{usedSeats}} seats and {{usedStorage}}.",
    downgradeOver: "Agents and members over the limit are paused when the change takes effect.",
    intervalBody: "Pay {{price}} once a year instead of {{monthly}} every month. Your yearly term starts once the payment goes through.",
    newPrice: "New price",
    chargedTo: "Charged to",
    hostedCheckout_paymongo: "Secure checkout (GCash, Maya or card)",
    hostedCheckout_stripe: "Secure card checkout (Stripe)",
    hostedCheckout_paypal: "PayPal",
    prorationNote: "Prorated: {{amount}} + {{tax}} {{taxLabel}} charged today for the {{days}} days left in this cycle. {{credits}} extra messages are added right away.",
    prorationNoteNoTax: "Prorated: {{amount}} charged today for the {{days}} days left in this cycle. {{credits}} extra messages are added right away.",
    trialNote: "Your free trial ends and {{plan}} starts today: {{amount}} + {{tax}} {{taxLabel}} is charged now for the first period. {{credits}} messages are included.",
    trialNoteNoTax: "Your free trial ends and {{plan}} starts today: {{amount}} is charged now for the first period. {{credits}} messages are included.",
    restartNote: "Your billing period restarts today on {{plan}}: {{amount}} + {{tax}} {{taxLabel}} is charged now, after crediting {{credit}} for the unused time. {{credits}} messages are included.",
    restartNoteNoTax: "Your billing period restarts today on {{plan}}: {{amount}} is charged now, after crediting {{credit}} for the unused time. {{credits}} messages are included.",
    downgradeNote: "Takes effect on {{date}}. No refund for the current cycle; {{credit}} credit goes to your next invoice.",
    previewError: "We couldn't calculate the price change.",
    confirmUpgrade: "Upgrade now",
    confirmDowngrade: "Downgrade on {{date}}",
    confirmInterval: "Continue to payment",
    upgradedToast: "Upgraded to {{plan}}. New limits are active now.",
    scheduledToast: "Downgrade to {{plan}} scheduled for {{date}}",
  },
  checkout: {
    redirecting: "Redirecting to secure payment…",
    unavailable: "Payments are temporarily unavailable. Please try again.",
    paid: "Payment received. Thank you!",
    failed: "The payment didn't go through. Nothing was charged.",
    returnSuccess: "Payment received. Your receipt will be emailed shortly.",
    returnCancelled: "Payment cancelled. Nothing was charged.",
    confirmPending: "Your payment is being confirmed. This page updates once it goes through.",
    devTitle: "Test payment",
    devBody: "Payments run in test mode on this environment. Choose how this checkout ends.",
    amount: "Amount",
    devPay: "Pay",
    devFail: "Simulate failure",
  },
  addons: {
    title: "Add-ons",
    subtitle: "Extra capacity on top of your plan, billed with your subscription",
    none: "No add-ons yet.",
    add: "Add",
    cancel: "Cancel",
    cancelled: "Add-on cancelled. It stays active until the end of this cycle.",
    ending: "Ending",
    endsOn: "ends {{date}}",
    oneTime: "one-time",
    subtotal: "Subtotal",
    orUpgrade: "Or upgrade",
    upgrade: "Upgrade",
    replacesLite: "Plus replaces Secure launch Lite at once. The unused part of Lite's current billing period is credited on this invoice.",
    compare: {
      title: "Compare Secure launch Lite and Plus",
      subtitle: "Both let your own portal hand signed-in customers into chat. Plus also uses what you know about them.",
      col_what: "What you get",
      col_lite: "Lite",
      col_plus: "Plus",
      current: "Your plan",
      included: "Included",
      notIncluded: "Not included",
      row_month: "Per month",
      row_year: "Per year",
      yearHint: "Two months free",
      row_launch: "Signed-in launch",
      launchHint: "One-time code, redeemed once, session limits, revoke.",
      row_named: "\"Chatting as\" and history",
      namedHint: "The chat shows the customer's name and keeps their chats together by customer reference.",
      row_facts: "Customer facts",
      factsHint: "Tier, account status and attributes used by the AI, flows, priority routing and compliance branches.",
      row_volume: "Launches per month",
      volumeLite: "{{limit}} per workspace",
      volumePlus: "Unlimited",
      row_plans: "Plans",
      plansBoth: "Standard and Pro",
      footnote: "Prices exclude VAT. Institution includes Plus at no charge. Buying Plus replaces Lite and credits its unused time.",
    },
    seatsIncluded: "{{plan}} · {{count}} seats included",
    dueNowDays: "Prorated: {{days}} of {{total}} days left",
    renews_month: "Charged now. Renews monthly with your plan.",
    renews_year: "Charged now for the full year. Renews yearly at {{price}} a unit.",
    renews_year_prorated: "Charged now for the {{days}} days left in your year. Renews yearly at {{price}} a unit.",
    name_message_pack: "Message pack",
    name_extra_agent: "Extra agent",
    name_extra_seat: "Extra seat",
    name_remove_branding: "Remove branding",
    name_knowledge_storage_1gb: "Extra knowledge storage (+1 GB)",
    name_secure_launch_lite: "Secure launch Lite",
    name_secure_launch: "Secure launch Plus",
    price_message_pack: "{{price}} per 2,000 messages",
    price_extra_agent: "{{price}} / agent / month",
    price_extra_seat: "{{price}} / seat / month",
    price_remove_branding: "{{price}} / month",
    price_knowledge_storage_1gb: "{{price}} / GB / month",
    price_extra_agent_year: "{{price}} / agent / year",
    price_extra_seat_year: "{{price}} / seat / year",
    price_remove_branding_year: "{{price}} / year",
    price_knowledge_storage_1gb_year: "{{price}} / GB / year",
    price_secure_launch_lite: "{{price}} / month · 2,000 launches",
    price_secure_launch: "{{price}} / month · unlimited launches",
    price_secure_launch_lite_year: "{{price}} / year · 2,000 launches a month",
    price_secure_launch_year: "{{price}} / year · unlimited launches",
    title_extra_seat: "Add seats",
    title_message_pack: "Buy message packs",
    title_extra_agent: "Add agents",
    title_remove_branding: "Remove branding",
    title_knowledge_storage_1gb: "Add knowledge storage",
    title_secure_launch_lite: "Add Secure launch Lite",
    title_secure_launch: "Add Secure launch Plus",
    quantity_extra_seat: "Extra seats",
    quantity_message_pack: "Packs of 2,000 messages",
    quantity_extra_agent: "Extra agents",
    quantity_remove_branding: "Quantity",
    quantity_knowledge_storage_1gb: "Extra storage",
    quantity_secure_launch_lite: "Quantity",
    quantity_secure_launch: "Quantity",
    unit_extra_seat: "Per seat",
    unit_message_pack: "Per pack",
    unit_extra_agent: "Per agent",
    unit_remove_branding: "Price",
    unit_knowledge_storage_1gb: "Per +1 GB",
    unit_secure_launch_lite: "Price",
    unit_secure_launch: "Price",
    body_extra_seat: "Add seats for more team members.",
    body_message_pack: "Extra messages for this cycle.",
    body_extra_agent: "Run more assistants at once.",
    body_remove_branding: "Hide the \"Powered by\" line in the widget and web app.",
    body_knowledge_storage_1gb: "More room for the files, pages and text your assistants answer from. Nothing you have already added is touched if you remove it later; you just can't add more while you are over the limit.",
    body_secure_launch_lite: "Let your own portal hand signed-in customers straight into chat, already named, with their chats kept together. Up to 2,000 launches a month. Customer facts (tier, account status, attributes) are not included.",
    body_secure_launch: "Everything in Lite, plus customer facts: tier, account status and attributes reach the AI, your flows, priority routing and compliance branches. Unlimited launches. Included in Institution.",
    confirm_extra_seat: "Add seats",
    confirm_message_pack: "Buy packs",
    confirm_extra_agent: "Add agents",
    confirm_remove_branding: "Remove branding",
    confirm_knowledge_storage_1gb: "Add storage",
    confirm_secure_launch_lite: "Add Secure launch Lite",
    confirm_secure_launch: "Add Secure launch Plus",
    bought_extra_seat_one: "{{count}} seat added",
    bought_extra_seat_other: "{{count}} seats added",
    bought_message_pack_one: "{{count}} message pack added",
    bought_message_pack_other: "{{count}} message packs added",
    bought_extra_agent_one: "{{count}} agent slot added",
    bought_extra_agent_other: "{{count}} agent slots added",
    bought_remove_branding: "Branding removed from the widget and web app",
    bought_knowledge_storage_1gb_one: "{{count}} GB of knowledge storage added",
    bought_knowledge_storage_1gb_other: "{{count}} GB of knowledge storage added",
    limitReached: "Limit reached for this workspace",
    capHint: "Up to {{max}} GB in total; you hold {{held}} GB. For more, ask sales about Institution.",
    capReached: "You hold the most this workspace can buy ({{max}} GB). For more, ask sales about Institution.",
    bought_secure_launch_lite: "Secure launch Lite added",
    bought_secure_launch: "Secure launch Plus added",
  },
  methods: {
    title: "Payment method",
    paypalOption: "PayPal",
    paypalIntro: "Pay with your PayPal account. You approve it once on PayPal, then we charge it for each renewal and top-up.",
    paypalDefault: "It becomes your default method for renewals and top-ups.",
    paypalContinue: "Continue to PayPal",
    paypalUnavailable: "PayPal isn't available right now. Try again later, or contact sales to pay another way.",
    onlyPaypal: "Pay by PayPal",
    otherOptionsBefore: "Other payment options:",
    otherOptionsAction: "contact sales",
    legacy: "Saved earlier · still used for renewals until you add PayPal",
    addPaypal: "Add payment method",
    setupWait: "Opening PayPal…",
    disabledError: "That payment method is no longer offered. Pay with PayPal, or contact sales to pay another way.",
    add: "Add method",
    addTitle: "Add payment method",
    typeLabel: "Payment method type",
    type_gcash: "GCash",
    type_maya: "Maya",
    type_card: "Credit / debit card",
    type_bank: "Bank transfer",
    label_card: "{{brand}} ending {{last4}}",
    label_gcash: "GCash ••• {{last4}}",
    label_maya: "Maya ••• {{last4}}",
    label_bank: "Bank transfer",
    label_paypal: "PayPal account",
    add_stripe: "Add card",
    add_paypal: "Add PayPal",
    via: "via {{gateway}}",
    otherCurrency: "Not used for your current billing currency",
    setupRedirect: "Redirecting to save your payment method…",
    setupLost: "We couldn't finish saving the payment method. Please add it again.",
    setupCancelled: "Saving the payment method was cancelled.",
    expires: "Expires {{date}}",
    backup: "Backup · used if the default is declined",
    defaultHint: "Used for renewals and top-ups",
    default: "Default",
    makeDefaultAction: "Make default",
    defaultSet: "Default payment method updated",
    remove: "Remove {{method}}",
    removed: "Payment method removed",
    emptyTitle: "No payment method yet",
    emptyBody: "Add a payment method so renewals and top-ups go through.",
    cardNumber: "Card number",
    expiry: "Expiry",
    cvc: "CVC",
    mobile_gcash: "GCash mobile number",
    mobile_maya: "Maya mobile number",
    mobileHint_gcash: "You'll approve a ₱1 verification in the GCash app. It's refunded right away.",
    mobileHint_maya: "You'll be redirected to Maya to link your wallet.",
    bankTitle: "Pay by bank transfer (BDO / BPI / UnionBank / InstaPay)",
    bankBody: "We'll email invoices with CRUD.IT Solutions Inc. bank details. Send proof of payment to the billing email on the invoice. Plans stay active for 7 days past the due date.",
    makeDefault: "Make this the default payment method",
    save: "Save payment method",
    saved: "Payment method saved",
    errors: {
      number: "Enter a valid card number.",
      expiry: "Use MM / YY.",
      cvc: "Enter the 3 or 4 digit code.",
      mobile: "Enter a PH mobile number (09xx xxx xxxx).",
      tokenize: "Your card couldn't be verified. Check the details and try again.",
      config: "We couldn't load the payment settings, so cards can't be saved yet.",
    },
  },
  manual: {
    title: "Billed by invoice · managed by Sagot",
    body: "Sagot invoices this workspace outside the product, so there is nothing to pay here and no card or PayPal account is charged. To change the plan, add-ons or how you pay, contact sales.",
    term: "Current term: {{start}} to {{end}}",
    agreed: "Agreed plan price: {{price}}",
    contact: "Contact sales",
    gated: "Billed by invoice. Contact sales to change this.",
    methodsTitle: "Billed by invoice",
    methodsBody: "No payment method is needed. Sagot sends the invoice and manages renewals for you.",
    badge: "Invoiced",
  },
  salesRequest: {
    title: "Contact sales",
    subtitle: "Tell us what you need and we'll reply by email. We can invoice you instead of charging PayPal.",
    plan: "Plan",
    planNone: "No plan, add-ons only",
    addons: "Add-ons",
    addonsHint: "Choose any you want on top of the plan.",
    seats: "Seats",
    seatsHint: "How many people will use the workspace? (optional)",
    cycle: "Billing cycle",
    monthly: "Monthly",
    annual: "Annual",
    notes: "Notes",
    notesHint: "Purchase order, how you prefer to pay, anything else.",
    contactName: "Your name",
    contactEmail: "Email",
    contactPhone: "Phone (optional)",
    send: "Send request",
    loadError: "We couldn't load the plans and add-ons. You can still describe what you need in the notes.",
    sendError: "We couldn't send your request. Your answers are still here; try again.",
    errors: {
      name: "Enter your name.",
      email: "Enter a valid email address.",
      phone: "Use digits, spaces, + or -.",
      nothing: "Choose a plan or add-ons, or tell us what you need in the notes.",
      seats: "Enter a whole number from 1 to 10,000.",
    },
    sent: {
      title: "Request sent",
      body: "Thank you. Our sales team will email {{email}} soon.",
      reference: "Reference SR-{{number}}",
      noMail: "Your request is saved. The notice email to sales didn't go out, but they will still see it.",
      done: "Done",
    },
    history: {
      title: "Your sales requests",
      status_new: "New",
      status_contacted: "Contacted",
      status_done: "Done",
      row: "SR-{{number}} · {{plan}}",
      noPlan: "Add-ons only",
    },
  },
  details: {
    title: "Billing details",
    subtitle: "Printed on official receipts (BIR-registered)",
    edit: "Edit",
    registeredName: "Registered name",
    tin: "TIN",
    tinHint: "e.g. 008-412-773-000",
    receiptEmail: "Receipt email",
    receiptsTo: "Receipts sent to",
    address: "Business address",
    line1: "Address line 1",
    line2: "Address line 2",
    addressHint: "Must match your BIR Certificate of Registration (Form 2303).",
    city: "City / municipality",
    province: "Province",
    zip: "ZIP code",
    taxType: "Tax type",
    taxUnset: "Not set",
    tax_vat_registered: "VAT-registered",
    tax_non_vat: "Non-VAT",
    tax_vat_exempt: "VAT-exempt",
    vatExempt: "VAT-exempt organization",
    vatExemptHint: "We'll ask for your BIR exemption certificate before receipts drop VAT.",
    vatExemptShort: "VAT-exempt",
    exemptionRef: "Exemption certificate reference",
    proof_pending: "pending proof",
    proof_verified: "verified",
    proof_rejected: "proof rejected",
    saved: "Billing details saved. Next official receipt will use them.",
    loadError: "Billing details couldn't load.",
    emptyTitle: "Add your billing details",
    emptyBody: "Your registered name, TIN and address appear on official receipts.",
    errors: {
      name: "Enter the registered name.",
      tin: "Enter a valid TIN (e.g. 008-412-773-000).",
      email: "Enter a valid email.",
      line1: "Enter the street address.",
      city: "Enter the city or municipality.",
    },
  },
  invoices: {
    title: "Invoices & official receipts",
    search: "Search invoices…",
    number: "Invoice no.",
    date: "Date",
    description: "Description",
    amount: "Amount",
    status: "Status",
    actions: "Actions",
    inclTax: "incl. {{tax}} {{label}}",
    payTitle: "Pay invoice {{number}}",
    payAmount: "Pay {{amount}}",
    or: "OR {{number}}",
    status_paid: "Paid",
    status_due: "Due {{date}}",
    status_overdue: "Overdue since {{date}}",
    status_failed: "Payment failed",
    status_void: "Void",
    status_draft: "Draft",
    payNow: "Pay now",
    pdf: "PDF",
    paid: "Invoice paid. Thank you!",
    loadError: "Invoices couldn't load.",
    emptyTitle: "No invoices yet",
    emptyBody: "Invoices and official receipts appear here after your first payment.",
    noMatchTitle: "No invoices match",
    noMatchBody: "Try a different invoice number or description.",
    clear: "Clear search",
  },
  breakdown: {
    title: "Usage breakdown",
    byAgent: "By agent",
    byChannel: "By channel",
    byFeature: "By feature",
    agent: "Agent",
    channel: "Channel",
    feature: "Feature",
    features: { answer: "Answers", workflow_summary: "Workflow summaries" },
    answers: "Answers",
    credits: "Messages",
    share: "Share of messages",
    total: "Total",
    loadError: "Usage couldn't load.",
    emptyTitle: "No usage yet this cycle",
    emptyBody: "Answers from your assistants show up here as they happen.",
  },
  channels: {
    widget: "Website widget",
    messenger: "Facebook Messenger",
    webapp: "Web app",
    api: "API",
    mobile: "Mobile app",
    email: "Email",
    playground: "Playground",
  },
  export: {
    group: "Group",
    key: "Item",
    done: "Usage report exported: {{file}}",
  },
} as const;
